import { Injectable, NgZone, OnDestroy } from "@angular/core";
import { MatSnackBar, MatSnackBarRef } from "@angular/material/snack-bar";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
  getChipLogo,
  InsurerProgressItem,
  QuotationProgressPhase,
  SLOW_ON_ARRIVAL_STATUSES,
  SLOW_STATE_AFTER_MS,
  toProgressStatus
} from "src/app/share/constants/quotation-progress";
import { D2CQuotationInterface } from "src/app/share/interface/motor.interface";
import { ScreenSizeService } from "src/app/share/service/screen-size.service";
import { SlowQuoteToastComponent } from "./slow-quote-toast/slow-quote-toast.component";

export interface QuotationProgressView {
  items: InsurerProgressItem[];
  phase: QuotationProgressPhase;
  ready: InsurerProgressItem[];
  waiting: InsurerProgressItem[];
  unavailable: InsurerProgressItem[];
}

const HIGHLIGHT_DURATION_MS = 1000;
const SCROLL_GAP_PX = 16;

// Scoped to one visit of the quotation page (provided by PlansComponent), so the
// slow timers and the "toast shown" flag reset when the customer comes back.
@Injectable()
export class QuotationProgressService implements OnDestroy {
  private itemsSubject = new BehaviorSubject<InsurerProgressItem[]>([]);
  items$ = this.itemsSubject.asObservable();

  view$: Observable<QuotationProgressView> = this.items$.pipe(
    map((items) => {
      const ready = items.filter((x) => x.status === "ready");
      const waiting = items.filter((x) => x.status === "waiting");
      const unavailable = items.filter((x) => x.status === "unavailable");
      return {
        // Chips read left to right: Ready first, Unavailable always at the far right
        items: [...ready, ...waiting, ...unavailable],
        phase: this.getPhase(items),
        ready,
        waiting,
        unavailable
      };
    })
  );

  // Height of the fixed mobile footer, so scroll-to-card can keep cards above it
  bottomInset = 0;

  // The host page can turn off the mascot (affiliate /comparison has none)
  showMascot = true;

  private firstSeenWaitingAt = new Map<string, number>();
  private hasReceivedList = false;
  private toastShown = false;
  private toastRef: MatSnackBarRef<SlowQuoteToastComponent> | null = null;
  // False while the customer is past the comparison step (add-ons, etc.)
  private onComparisonStep = true;
  private slowTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private zone: NgZone,
    private snackBar: MatSnackBar,
    private screenSizeService: ScreenSizeService
  ) {}

  update(quotationList: D2CQuotationInterface[]): void {
    if (!Array.isArray(quotationList)) return;

    const previous = new Map(this.itemsSubject.value.map((x) => [x.id, x]));
    const now = Date.now();
    let readyOrder = 0;

    const items: InsurerProgressItem[] = [];
    quotationList.forEach((quotation) => {
      const status = toProgressStatus(quotation.status);
      if (!status) return;

      const prev = previous.get(quotation.id);
      if (status === "waiting" && !this.firstSeenWaitingAt.has(quotation.id)) {
        this.firstSeenWaitingAt.set(quotation.id, now);
      }

      // Only insurers that turn Ready during this visit get the tick entrance
      let itemReadyOrder: number | null = prev?.readyOrder ?? null;
      if (
        status === "ready" &&
        this.hasReceivedList &&
        prev?.status !== "ready"
      ) {
        itemReadyOrder = readyOrder++;
      }

      items.push({
        id: quotation.id,
        name: quotation.name,
        image: quotation.image,
        chipLogo: getChipLogo(quotation.code, quotation.name, quotation.image),
        code: quotation.code,
        status,
        isSlow: status === "waiting" && this.isSlow(quotation, now),
        readyOrder: itemReadyOrder
      });
    });

    this.hasReceivedList = true;
    this.emit(items);
  }

  scrollToInsurer(id: string): void {
    const anchor = document.querySelector<HTMLElement>(
      `[data-insurer-id="${id}"]`
    );
    if (!anchor) return;
    const card =
      anchor.querySelector<HTMLElement>("[data-progress-card]") ?? anchor;

    const header = document.querySelector<HTMLElement>(".header");
    const topOffset =
      (header?.getBoundingClientRect().height ?? 0) + SCROLL_GAP_PX;
    const bottomLimit = window.innerHeight - this.bottomInset;
    const rect = card.getBoundingClientRect();

    if (rect.top >= topOffset && rect.bottom <= bottomLimit) {
      this.highlight(card);
      return;
    }

    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    window.scrollTo({
      top: window.scrollY + rect.top - topOffset,
      behavior: reduceMotion ? "auto" : "smooth"
    });
    this.afterScroll(() => this.highlight(card));
  }

  // The toast belongs to the comparison step only: close it when the customer
  // moves on, and let it show on return if the quotes turned slow meanwhile
  setOnComparisonStep(onComparisonStep: boolean): void {
    if (this.onComparisonStep === onComparisonStep) return;
    this.onComparisonStep = onComparisonStep;

    if (!onComparisonStep) {
      this.toastRef?.dismiss();
      return;
    }
    const items = this.itemsSubject.value;
    if (this.getPhase(items) === "slow") this.showToastOnce(items);
  }

  ngOnDestroy(): void {
    this.stopSlowTimer();
    this.toastRef?.dismiss();
  }

  private emit(items: InsurerProgressItem[]): void {
    this.itemsSubject.next(items);

    if (items.some((x) => x.status === "waiting" && !x.isSlow)) {
      this.startSlowTimer();
    } else {
      this.stopSlowTimer();
    }

    if (this.getPhase(items) === "slow") this.showToastOnce(items);
  }

  private getPhase(items: InsurerProgressItem[]): QuotationProgressPhase {
    const waiting = items.filter((x) => x.status === "waiting");
    if (waiting.length === 0) return "finished";
    return waiting.some((x) => x.isSlow) ? "slow" : "loading";
  }

  private isSlow(quotation: D2CQuotationInterface, now: number): boolean {
    if (SLOW_ON_ARRIVAL_STATUSES.includes(quotation.status)) return true;
    const firstSeen = this.firstSeenWaitingAt.get(quotation.id);
    return firstSeen != null && now - firstSeen >= SLOW_STATE_AFTER_MS;
  }

  // Ticks outside Angular and only re-enters when an insurer actually turns slow
  private startSlowTimer(): void {
    if (this.slowTimer) return;
    this.zone.runOutsideAngular(() => {
      this.slowTimer = setInterval(() => {
        const now = Date.now();
        const items = this.itemsSubject.value;
        const turnedSlow = items.some(
          (x) =>
            x.status === "waiting" &&
            !x.isSlow &&
            now - (this.firstSeenWaitingAt.get(x.id) ?? now) >=
              SLOW_STATE_AFTER_MS
        );
        if (!turnedSlow) return;

        this.zone.run(() =>
          this.emit(
            items.map((x) =>
              x.status === "waiting" && !x.isSlow
                ? {
                    ...x,
                    isSlow:
                      now - (this.firstSeenWaitingAt.get(x.id) ?? now) >=
                      SLOW_STATE_AFTER_MS
                  }
                : x
            )
          )
        );
      }, 1000);
    });
  }

  private stopSlowTimer(): void {
    if (!this.slowTimer) return;
    clearInterval(this.slowTimer);
    this.slowTimer = null;
  }

  // Desktop only, comparison step only, once per visit, and only while something
  // is still waiting. No duration: it stays until the customer closes it.
  private showToastOnce(items: InsurerProgressItem[]): void {
    if (
      this.toastShown ||
      !this.onComparisonStep ||
      this.screenSizeService.isMobileSubject.value
    )
      return;
    const waitingCount = items.filter((x) => x.status === "waiting").length;
    if (waitingCount === 0) return;

    this.toastShown = true;
    this.toastRef = this.snackBar.openFromComponent(SlowQuoteToastComponent, {
      verticalPosition: "top",
      horizontalPosition: "right",
      panelClass: "slow-quote-toast-panel",
      data: {
        anyReady: items.some((x) => x.status === "ready"),
        showMascot: this.showMascot,
        waitingCount
      }
    });
    this.toastRef.afterDismissed().subscribe(() => (this.toastRef = null));
  }

  private afterScroll(callback: () => void): void {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      window.removeEventListener("scrollend", finish);
      callback();
    };
    // scrollend isn't available on older Safari, so fall back to a timeout
    window.addEventListener("scrollend", finish, { once: true });
    setTimeout(finish, 800);
  }

  private highlight(card: HTMLElement): void {
    card.classList.remove("insurer-card-highlight");
    // Force a reflow so the pulse restarts when the same chip is clicked twice
    void card.offsetWidth;
    card.classList.add("insurer-card-highlight");
    setTimeout(
      () => card.classList.remove("insurer-card-highlight"),
      HIGHLIGHT_DURATION_MS
    );
  }
}
